import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import api, { formatApiError } from "../lib/api";
import { downloadPdf, studioLines } from "../lib/letterPdf";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Slider } from "./ui/slider";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import {
  X, Plus, Trash2, Save, Printer, Calculator, RefreshCw, ExternalLink, Building2,
} from "lucide-react";
import { toast } from "sonner";
import { formatEuroIt } from "../lib/euro";
import { formatItInput, toItInputDisplay, formatNumIt } from "../lib/format";
import ListinoSpeseDialog from "./ListinoSpeseDialog";

const eur = (n) => `${formatNumIt(Number.isFinite(n) ? n : 0)} €`;
const num = (v) => {
  if (typeof v === "number") return v;
  const n = parseFloat(String(v ?? "").replace(/\./g, "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

// input €/numerico compatto
function Field({ label, value, onChange, type = "number", suffix, hint, testId, className = "", money, title }) {
  const [focused, setFocused] = useState(false);
  const [buf, setBuf] = useState("");
  const isMoney = money || suffix === "€";
  const shown = isMoney ? (focused ? buf : formatEuroIt(value)) : (value ?? "");
  return (
    <label className={`block ${className}`} title={title || undefined}>
      <span className="block text-[11px] uppercase tracking-wide text-slate-400 mb-1">{label}</span>
      <div className="flex items-center gap-1">
        <div className="relative flex-1">
        <Input
          type={isMoney ? "text" : (type === "number" ? "number" : type)}
          value={shown}
          onFocus={() => { if (isMoney) setBuf(toItInputDisplay(value)); setFocused(true); }}
          onBlur={() => setFocused(false)}
          onChange={(e) => { if (isMoney) { const f = formatItInput(e.target.value); setBuf(f); onChange(f); } else { onChange(e.target.value); } }}
          data-testid={testId}
          className={`h-9 bg-slate-900/70 border-slate-700 text-slate-100 ${isMoney ? "pr-7 text-right font-mono" : ""}`}
        />
        {isMoney && <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">€</span>}
        </div>
        {suffix && !isMoney && <span className="text-xs text-slate-400">{suffix}</span>}
      </div>
      {hint && <span className="block text-[10px] text-slate-500 mt-0.5">{hint}</span>}
    </label>
  );
}

function MoneyInput({ value, onChange, testId, className }) {
  const [f, setF] = useState(false);
  const [buf, setBuf] = useState("");
  return (
    <div className="relative">
      <Input value={f ? buf : formatEuroIt(value)} onFocus={() => { setBuf(toItInputDisplay(value)); setF(true); }} onBlur={() => setF(false)} onChange={(e) => { const v = formatItInput(e.target.value); setBuf(v); onChange(v); }} data-testid={testId} className={`${className || ""} pr-6 text-right font-mono`} />
      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">€</span>
    </div>
  );
}

function Calc({ label, value, testId }) {
  return (
    <div>
      <span className="block text-[11px] uppercase tracking-wide text-slate-400 mb-1">{label}</span>
      <div data-testid={testId} className="h-9 flex items-center px-3 rounded-md bg-emerald-500/10 border border-emerald-600/30 text-emerald-300 font-mono text-sm">
        {eur(value)}
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
    <h4 className="text-sm font-semibold text-amber-300 mb-3">{title}</h4>
    {children}
  </div>
);

const EMPTY = {
  spese: { righe: [{ descrizione: "", spese: "" }] },
  micro: {},
  macro: {},
  materiale: { deprezzamento: false, corresponsabilita: false },
  datore: { altre: [{ d: "", v: "" }, { d: "", v: "" }, { d: "", v: "" }] },
  inail: { sesso: "M" },
  carrozzeria: {},
};

const DAYS = [
  ["g100", "Giorni 100%", 1.0],
  ["g75", "Giorni 75%", 0.75],
  ["g50", "Giorni 50%", 0.5],
  ["g25", "Giorni 25%", 0.25],
];

export default function QuantificazioneDialog({ open, onOpenChange, pratica, assistitoLabel }) {
  const [q, setQ] = useState(EMPTY);
  const [prefill, setPrefill] = useState({});
  const [tab, setTab] = useState({});
  const [medici, setMedici] = useState([]);
  const [carrozzieri, setCarrozzieri] = useState([]);
  const [periti, setPeriti] = useState([]);
  const [saving, setSaving] = useState(false);
  const [tabKey, setTabKey] = useState("spese");
  const [anno, setAnno] = useState("");
  const [tinvTab, setTinvTab] = useState({ micro: {}, macro: {} });
  useEffect(() => {
    if (!open || !anno) { return; }
    Promise.all(["micro", "macro"].map((t) => api.get(`/studio/tabelle-invalidita/${t}/${anno}`).then(({ data }) => data.valori || {}).catch(() => ({}))))
      .then(([micro, macro]) => setTinvTab({ micro, macro }));
  }, [open, anno]);
  const [listino, setListino] = useState([]);
  const [listinoOpen, setListinoOpen] = useState(false);
  const pid = pratica?.id;

  const loadListino = useCallback(async () => {
    try { const { data } = await api.get("/studio/listino-spese"); setListino(data || []); } catch { /* noop */ }
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/studio/pratiche/${pid}/quantificazione`);
      setPrefill(data.prefill || {});
      setTab(data.tabelle || {});
      setMedici(data.medici_legali || []);
      setCarrozzieri(data.carrozzieri || []);
      setPeriti(data.periti || []);
      loadListino();
      const d = data.data || {};
      const pe = data.prefill?.eta ?? "";
      setAnno(d.anno || data.tabelle?.anno_default || "");
      setQ({
        spese: d.spese || EMPTY.spese,
        micro: { ...(d.micro || {}), eta: (d.micro && d.micro.eta) || pe || "" },
        macro: { ...(d.macro || {}), eta: (d.macro && d.macro.eta) || pe || "" },
        materiale: d.materiale || EMPTY.materiale,
        datore: d.datore || EMPTY.datore,
        inail: d.inail || { sesso: "M" },
        carrozzeria: d.carrozzeria || {},
      });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [pid]);

  useEffect(() => { if (open && pid) load(); }, [open, pid, load]);

  const setT = (tabName, patch) => setQ((s) => ({ ...s, [tabName]: { ...s[tabName], ...patch } }));

  const addFromListino = (vid) => {
    const item = listino.find((x) => x.id === vid);
    if (!item) return;
    setT("spese", { righe: [...(q.spese.righe || []), { descrizione: item.descrizione, spese: String(item.importo ?? "") }] });
  };

  const save = async (tabName) => {
    setSaving(true);
    try {
      const extra = tabName === "micro" ? { totale: mFinale } : tabName === "macro" ? { totale: MRis } : {};
      await api.put(`/studio/pratiche/${pid}/quantificazione`, { [tabName]: { ...q[tabName], ...extra } });
      toast.success("Quantificazione salvata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  // ---- Totali riusabili ----
  const speseTot = (q.spese?.righe || []).reduce((s, r) => s + num(r.spese), 0);

  // Anno tabelle selezionato + coefficienti di capitalizzazione (invalidità permanente specifica)
  const yr = (tab.storico && tab.storico[anno]) || {};
  const changeAnno = (a) => { setAnno(a); if (pid) api.put(`/studio/pratiche/${pid}/quantificazione`, { anno: a }).catch(() => {}); };
  const coeffCapForAge = (age) => {
    const t = tab.coeff_capitalizzazione || [];
    if (!t.length || !age) return "";
    let best = t[0];
    for (const r of t) { if (r.eta <= age) best = r; else break; }
    return best.coeff;
  };

  // ===================== TAB 2 — MICRO =====================
  const mc = yr.micro || { punto_base: 0, demoltiplicatore_eta: 0.005, itt_giornaliero: 0 };
  const m = q.micro;
  const mCoeffEta = Math.max(0, 1 - num(mc.demoltiplicatore_eta) * Math.max(0, num(m.eta) - 11));
  const mPuntoBase = num(m.punto_base_override) || num(mc.punto_base);
  const mItt = num(m.itt_override) || num(mc.itt_giornaliero);
  const mTriplo = num(m.reddito_annuo) * 3;
  const mPermTab = !num(m.punto_base_override) ? (tinvTab.micro?.[String(Math.round(num(m.eta)))] || {})[String(num(m.perm_punti))] : undefined;
  const mPermTot = mPermTab != null && num(m.perm_punti) > 0 ? num(mPermTab) : num(m.perm_punti) * mPuntoBase * mCoeffEta;
  const mAggTot = num(m.agg_punto) * mPuntoBase * mCoeffEta;
  const mTemp = DAYS.map(([k, , f]) => ({ k, tot: num(m[`temp_${k}`]) * mItt * f }));
  const mTempTot = mTemp.reduce((s, x) => s + x.tot, 0);
  const mBiologico = mPermTot + mAggTot + mTempTot;
  const mMoraleTot = mBiologico * (num(m.morale_pct) / 100);
  const mLucroTot = num(m.lucro_giorni) * num(m.lucro_redd_gg);
  const mIpsTemp = DAYS.map(([k, , f]) => ({ k, tot: num(m[`ipst_${k}`]) * mItt * f }));
  const mIpsTempTot = mIpsTemp.reduce((s, x) => s + x.tot, 0);
  const mIpsTot = num(m.reddito_annuo) * (num(m.ips_postumi) / 100) * (num(m.ips_vita_lavoro) / 100) * (num(m.ips_coeff_eta) || 1);
  const mNonPatr = mBiologico + mMoraleTot;
  const mLordo = mNonPatr + speseTot + mLucroTot + mIpsTempTot + mIpsTot;
  const mConcorso = mLordo * (1 - num(m.concorso_pct) / 100);
  const mDiff = mConcorso - num(m.acconto);
  const mFinale = mDiff + num(m.arrotondamento);

  // ===================== TAB 3 — MACRO =====================
  const ma = yr.macro || { punto_base_bio: 0, itt_giornaliero: 0, demoltiplicatore_eta: 0.005 };
  const M = q.macro;
  const MCoeffEta = Math.max(0, 1 - num(ma.demoltiplicatore_eta) * Math.max(0, num(M.eta) - 11));
  const MPuntoBase = num(M.punto_base_override) || num(ma.punto_base_bio);
  const MItt = num(M.itt_override) || num(ma.itt_giornaliero);
  const MPermTab = !num(M.punto_base_override) ? (tinvTab.macro?.[String(Math.round(num(M.eta)))] || {})[String(num(M.punti))] : undefined;
  const MPermBio = MPermTab != null && num(M.punti) > 0 ? num(MPermTab) : num(M.punti) * MPuntoBase * MCoeffEta;
  const MTemp = DAYS.map(([k, , f]) => ({ k, tot: num(M[`temp_${k}`]) * MItt * f }));
  const MTempTot = MTemp.reduce((s, x) => s + x.tot, 0);
  const MMoralePerm = MPermBio * (num(M.morale_perm_pct) / 100);
  const MMoraleTemp = MTempTot * (num(M.morale_temp_pct) / 100);
  const MLucroTot = num(M.lucro_giorni) * num(M.lucro_redd_gg);
  const MIpsTempTot = num(M.ipst_valore) * (num(M.ipst_pct) / 100 || 1);
  const MIpsTot = num(M.reddito) * (num(M.ips_postumi) / 100) * (num(M.ips_vita_lavoro) / 100) * (num(M.ips_coeff_eta) || 1);
  const MPersBase = M.pers_solo_perm ? MPermBio : MPermBio + MTempTot;
  const MPersTot = MPersBase * (num(M.pers_pct) / 100);
  const MNonPatr = MPermBio + MTempTot + MMoralePerm + MMoraleTemp + MPersTot;
  const MLordo = MNonPatr + speseTot + MLucroTot + MIpsTempTot + MIpsTot;
  const MConcorso = MLordo * (1 - num(M.concorso_pct) / 100);
  const MRis = MConcorso - num(M.acconto) + num(M.arrotondamento);

  // ===================== TAB 4 — MATERIALE =====================
  const mt = q.materiale;
  const fermoTot = num(mt.fermo_giorni) * num(mt.fermo_costo);
  const deprVoci = ["valore_relitto", "bollo", "recupero", "rottamazione", "immatricolazione", "passaggio", "spesa", "rientro", "deposito"];
  const deprTot = mt.deprezzamento ? deprVoci.reduce((s, k) => s + num(mt[k]), 0) : 0;
  const matBase = num(mt.fattura) + num(mt.quant_parte) + fermoTot + deprTot;
  const matNetto = mt.corresponsabilita ? matBase * (1 - num(mt.corresp_pct) / 100) : matBase;

  // ===================== TAB 5 — DATORE =====================
  const dt = q.datore;
  const datoreVoci = ["integrazione", "tredicesima", "quattordicesima", "ferie", "exfestivita", "permessi", "festivita", "tfr", "inail", "previdenziali"];
  const datoreTot = datoreVoci.reduce((s, k) => s + num(dt[k]), 0) + (dt.altre || []).reduce((s, r) => s + num(r.v), 0);

  // ===================== TAB 6 — INAIL =====================
  const inl = q.inail;
  const inCoeff = (tab.inail_patrimoniale_coeff || []).find((c) => num(inl.invalidita) >= c.da && num(inl.invalidita) <= c.a);
  const inQuota = tab.inail_quota_integrativa || 0.05;
  const inPatr = inCoeff ? num(inl.retribuzione) * inCoeff.coeff * (1 + inQuota * num(inl.familiari)) : 0;
  const inBio = num(inl.importo_biologico);
  const inTot = inBio + inPatr;

  // ===================== TAB 7 — CARROZZERIA =====================
  const cz = q.carrozzeria;
  const czTot = num(cz.pattuito) * (1 - num(cz.scorporo_pct) / 100);

  // ---- STAMPA tab attivo ----
  const printTab = async () => {
    const rows = buildPrintRows(tabKey, {
      q, speseTot, mTriplo, mPermTot, mAggTot, mTempTot, mMoraleTot, mLucroTot, mIpsTot, mNonPatr, mConcorso, mFinale,
      MPermBio, MTempTot, MMoralePerm, MMoraleTemp, MPersTot, MLucroTot, MIpsTot, MNonPatr, MRis,
      fermoTot, deprTot, matNetto, datoreTot, inPatr, inBio, inTot, czTot, prefill, medici,
    });
    const title = TABS.find((t) => t.k === tabKey)?.label || "";
    const html = `<div class="pgp-doc"><h3 style="text-align:center">QUANTIFICAZIONE DEL DANNO — ${escape2(title)}</h3>`
      + `<p><strong>Assistito:</strong> ${escape2(prefill.assistito || assistitoLabel)} &nbsp; <strong>Pratica:</strong> ${escape2(prefill.numero_pratica || "")}</p>`
      + `<table><tbody>${rows.map((r) => `<tr><td>${escape2(r[0])}</td><td style="text-align:right">${escape2(r[1])}</td></tr>`).join("")}</tbody></table></div>`;
    try {
      await downloadPdf(`Quantificazione_${(prefill.numero_pratica || "pratica").replace(/[^\w-]/g, "_")}_${tabKey}.pdf`, html, false);
      toast.success("PDF generato");
    } catch { toast.error("Generazione PDF non riuscita"); }
  };

  useEffect(() => {
    const c = coeffCapForAge(num(m.eta));
    if (c !== "" && String(c) !== String(m.ips_coeff_eta)) setT("micro", { ips_coeff_eta: c });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [m.eta, anno]);
  useEffect(() => {
    const c = coeffCapForAge(num(M.eta));
    if (c !== "" && String(c) !== String(M.ips_coeff_eta)) setT("macro", { ips_coeff_eta: c });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [M.eta, anno]);

  if (!open) return null;

  const HeaderBar = (
    <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800 bg-[#0d111a]">
      <div className="flex items-center gap-2 min-w-0">
        <Calculator className="h-5 w-5 text-amber-400 shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-100 truncate">
            QUANTIFICAZIONE DEL DANNO PER L'ASSISTITO {(prefill.assistito || assistitoLabel || "").toUpperCase()}
          </div>
          <div className="text-[11px] text-slate-400 truncate">SULLA PRATICA {prefill.numero_pratica || ""}</div>
        </div>
      </div>
      <button onClick={() => onOpenChange(false)} data-testid="quant-close" className="text-slate-400 hover:text-slate-100"><X className="h-5 w-5" /></button>
    </div>
  );

  const SaveBtn = (tabName) => (
    <Button onClick={() => save(tabName)} disabled={saving} data-testid={`quant-save-${tabName}`} className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
      <Save className="h-4 w-4 mr-1" /> Salva
    </Button>
  );
  const PrintBtn = (
    <Button onClick={printTab} variant="outline" data-testid="quant-print" className="border-slate-700 text-slate-200 hover:bg-slate-800">
      <Printer className="h-4 w-4 mr-1" /> Stampa
    </Button>
  );
  const CompagniaBtn = (tabName) => (
    <Button onClick={() => setT(tabName, { compagnia_open: !q[tabName].compagnia_open })} variant="outline" data-testid={`quant-compagnia-${tabName}`} className="border-slate-700 text-slate-200 hover:bg-slate-800">
      <Building2 className="h-4 w-4 mr-1" /> Quantificazione Compagnia
    </Button>
  );
  const MedicoSelect = (tabName) => (
    <label className="flex-1 min-w-[220px]">
      <span className="block text-[11px] uppercase tracking-wide text-slate-400 mb-1">Medico legale di parte</span>
      <select value={q[tabName].medico || ""} onChange={(e) => setT(tabName, { medico: e.target.value })} data-testid={`quant-medico-${tabName}`}
        className="w-full h-9 rounded-md bg-slate-900/70 border border-slate-700 text-slate-100 px-2 text-sm">
        <option value="">—</option>
        {medici.map((x) => <option key={x} value={x}>{x}</option>)}
      </select>
    </label>
  );

  return createPortal(
    <div className="fixed inset-0 z-[9998] bg-black/70 backdrop-blur-sm flex items-stretch" data-testid="quantificazione-dialog">
      <div className="m-auto w-full h-full max-w-[1200px] bg-[#0a0d14] border border-slate-800 flex flex-col sm:my-4 sm:h-[calc(100%-2rem)] sm:rounded-xl overflow-hidden quant-modal">
        {HeaderBar}
        <Tabs value={tabKey} onValueChange={setTabKey} className="flex-1 flex flex-col min-h-0">
          <TabsList className="flex-wrap justify-start gap-1 bg-[#0d111a] border-b border-slate-800 rounded-none px-3 py-2 h-auto">
            {TABS.map((t) => (
              <TabsTrigger key={t.k} value={t.k} data-testid={`quant-tab-${t.k}`}
                className="data-[state=active]:bg-amber-500 data-[state=active]:text-[#0a0d14] text-slate-300 text-xs">
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {/* ---------- TAB 1 SPESE MEDICHE ---------- */}
            <TabsContent value="spese" className="mt-0 space-y-4">
              <div className="flex flex-wrap items-center gap-2" data-testid="quant-listino-bar">
                <select onChange={(e) => { addFromListino(e.target.value); e.target.value = ""; }} defaultValue="" data-testid="quant-spese-listino-select"
                  className="h-9 bg-slate-900/60 border border-slate-700 rounded-md text-slate-100 px-3 text-sm">
                  <option value="">+ Aggiungi da Listino…</option>
                  {listino.map((v) => <option key={v.id} value={v.id}>{v.descrizione}{v.importo ? ` — ${formatEuroIt(v.importo)} €` : ""}</option>)}
                </select>
                <Button variant="outline" size="sm" onClick={() => setListinoOpen(true)} data-testid="quant-listino-gestisci" className="border-slate-700 text-slate-200 hover:bg-slate-800">Gestisci Listino</Button>
              </div>
              <Section title="Spese mediche">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-slate-400 text-xs uppercase">
                    <th className="pb-2">Descrizione</th><th className="pb-2 w-40">Spese (€)</th><th className="w-10" />
                  </tr></thead>
                  <tbody>
                    {(q.spese.righe || []).map((r, i) => (
                      <tr key={i}>
                        <td className="py-1 pr-2">
                          <Input value={r.descrizione} onChange={(e) => { const rr = [...q.spese.righe]; rr[i] = { ...rr[i], descrizione: e.target.value }; setT("spese", { righe: rr }); }}
                            data-testid={`quant-spese-desc-${i}`} className="h-9 bg-slate-900/70 border-slate-700 text-slate-100" />
                        </td>
                        <td className="py-1 pr-2">
                          <MoneyInput value={r.spese} onChange={(val) => { const rr = [...q.spese.righe]; rr[i] = { ...rr[i], spese: val }; setT("spese", { righe: rr }); }}
                            testId={`quant-spese-imp-${i}`} className="h-9 bg-slate-900/70 border-slate-700 text-slate-100" />
                        </td>
                        <td className="py-1">
                          <button onClick={() => { const rr = q.spese.righe.filter((_, j) => j !== i); setT("spese", { righe: rr.length ? rr : [{ descrizione: "", spese: "" }] }); }}
                            data-testid={`quant-spese-del-${i}`} className="text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex items-center justify-between mt-3">
                  <Button variant="outline" size="sm" onClick={() => setT("spese", { righe: [...q.spese.righe, { descrizione: "", spese: "" }] })}
                    data-testid="quant-spese-add" className="border-slate-700 text-slate-200 hover:bg-slate-800"><Plus className="h-4 w-4 mr-1" /> Aggiungi riga</Button>
                  <div className="text-sm text-slate-300">Totale spese: <span className="font-mono text-emerald-300" data-testid="quant-spese-tot">{eur(speseTot)}</span></div>
                </div>
              </Section>
              <div className="flex justify-end">{SaveBtn("spese")}</div>
            </TabsContent>

            {/* ---------- TAB 2 MICRO ---------- */}
            <TabsContent value="micro" className="mt-0">
              {TabelleInfo({ agg: yr.aggiornamento?.micro, fonte: tab.fonti?.micro, testKey: "micro", anno, anni: tab.anni, onAnno: changeAnno })}
              <div className="grid lg:grid-cols-2 gap-4">
                <div className="space-y-4">
                  <Section title="Dati">
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Età" value={m.eta} onChange={(v) => setT("micro", { eta: v })} title={(!prefill.eta && !m.eta) ? "Inserire data di nascita cliente e data sinistro nella pratica" : undefined} hint="auto da data nascita e data sinistro" testId="quant-micro-eta" />
                      <Field label="Reddito annuo" value={m.reddito_annuo} onChange={(v) => setT("micro", { reddito_annuo: v })} suffix="€" testId="quant-micro-reddito" />
                      <Calc label="Triplo reddito annuo" value={mTriplo} testId="quant-micro-triplo" />
                      <Field label="Punto base (mod.)" value={m.punto_base_override ?? mc.punto_base} onChange={(v) => setT("micro", { punto_base_override: v })} suffix="€" hint={`coeff. età ${mCoeffEta.toFixed(3)}`} testId="quant-micro-puntobase" />
                    </div>
                  </Section>
                  <Section title="Danno permanente">
                    <div className="grid grid-cols-2 gap-3">
                      <label className="block">
                        <span className="block text-[11px] uppercase tracking-wide text-slate-400 mb-1">Punti %</span>
                        <select value={m.perm_punti ?? ""} onChange={(e) => setT("micro", { perm_punti: e.target.value })} data-testid="quant-micro-punti" className="h-9 w-full bg-slate-900/70 border border-slate-700 rounded-md text-slate-100 px-3 text-sm">
                          {Array.from({ length: 10 }, (_, i) => i).map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                      </label>
                      <Calc label="Totale" value={mPermTot} testId="quant-micro-perm-tot" />
                      {mPermTab != null && num(m.perm_punti) > 0 && <div className="col-span-2 -mt-2 text-[11px] text-emerald-400" data-testid="quant-micro-da-tabella">Valore da Tabella MICRO {anno} (età {Math.round(num(m.eta))}, {num(m.perm_punti)} punti)</div>}
                      <Field label="Aggravamento sul punto" value={m.agg_punto} onChange={(v) => setT("micro", { agg_punto: v })} testId="quant-micro-agg" />
                      <Calc label="Totale" value={mAggTot} testId="quant-micro-agg-tot" />
                    </div>
                  </Section>
                  <Section title="Danno temporaneo">
                    {DAYS.map(([k, lbl], i) => (
                      <div key={k} className="grid grid-cols-2 gap-3 mb-2">
                        <Field label={lbl} value={m[`temp_${k}`]} onChange={(v) => setT("micro", { [`temp_${k}`]: v })} testId={`quant-micro-temp-${k}`} />
                        <Calc label="Totale" value={mTemp[i].tot} testId={`quant-micro-temp-tot-${k}`} />
                      </div>
                    ))}
                  </Section>
                  <Section title="Danno morale">
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="%" value={m.morale_pct} onChange={(v) => setT("micro", { morale_pct: v })} testId="quant-micro-morale" />
                      <Calc label="Totale" value={mMoraleTot} testId="quant-micro-morale-tot" />
                    </div>
                  </Section>
                </div>
                <div className="space-y-4">
                  <Section title="Lucro cessante">
                    <div className="grid grid-cols-3 gap-3">
                      <Field label="Giorni" value={m.lucro_giorni} onChange={(v) => setT("micro", { lucro_giorni: v })} testId="quant-micro-lucro-gg" />
                      <Field label="Redd/Gg" value={m.lucro_redd_gg} onChange={(v) => setT("micro", { lucro_redd_gg: v })} suffix="€" testId="quant-micro-lucro-redd" />
                      <Calc label="Totale" value={mLucroTot} testId="quant-micro-lucro-tot" />
                    </div>
                  </Section>
                  <Section title="Invalidità perm. specifica temporanea">
                    {DAYS.map(([k, lbl], i) => (
                      <div key={k} className="grid grid-cols-2 gap-3 mb-2">
                        <Field label={lbl} value={m[`ipst_${k}`]} onChange={(v) => setT("micro", { [`ipst_${k}`]: v })} testId={`quant-micro-ipst-${k}`} />
                        <Calc label="Totale" value={mIpsTemp[i].tot} testId={`quant-micro-ipst-tot-${k}`} />
                      </div>
                    ))}
                  </Section>
                  <Section title="Invalidità permanente specifica">
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Coefficiente età" value={m.ips_coeff_eta} onChange={(v) => setT("micro", { ips_coeff_eta: v })} hint="auto da tab. capitalizzazione (R.D. 1403/1922) — modificabile" testId="quant-micro-ips-coeff" />
                      <Field label="Postumi %" value={m.ips_postumi} onChange={(v) => setT("micro", { ips_postumi: v })} testId="quant-micro-ips-postumi" />
                      <Field label="Vita/lavoro %" value={m.ips_vita_lavoro} onChange={(v) => setT("micro", { ips_vita_lavoro: v })} testId="quant-micro-ips-vita" />
                      <Calc label="Totale" value={mIpsTot} testId="quant-micro-ips-tot" />
                    </div>
                  </Section>
                  <Section title="Concorso di colpa">
                    <Field label="%" value={m.concorso_pct} onChange={(v) => setT("micro", { concorso_pct: v })} testId="quant-micro-concorso" className="max-w-[140px]" />
                  </Section>
                </div>
              </div>
              <Section title="Riepilogo totali">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  <Calc label="Totale spese (da Spese Mediche)" value={speseTot} testId="quant-micro-rsp" />
                  <Calc label="Danno morale" value={mMoraleTot} testId="quant-micro-rmorale" />
                  <Calc label="Tot. danno non patr. (ex biologico)" value={mNonPatr} testId="quant-micro-rnonpatr" />
                  <Calc label="Totale quantificazione" value={mConcorso} testId="quant-micro-rtot" />
                  <Field label="Acconto su danno ricevuto" value={m.acconto} onChange={(v) => setT("micro", { acconto: v })} suffix="€" testId="quant-micro-acconto" />
                  <Calc label="Differenza su maggior danno" value={mDiff} testId="quant-micro-diff" />
                  <Field label="Arrotondamento" value={m.arrotondamento} onChange={(v) => setT("micro", { arrotondamento: v })} suffix="€" testId="quant-micro-arrot" />
                  <Calc label="Totale finale" value={mFinale} testId="quant-micro-finale" />
                </div>
              </Section>
              <Section title="Note">
                <Textarea value={m.note || ""} onChange={(e) => setT("micro", { note: e.target.value })} data-testid="quant-micro-note" className="bg-slate-900/70 border-slate-700 text-slate-100" rows={3} />
              </Section>
              <div className="flex flex-wrap items-end gap-3 justify-between">
                {MedicoSelect("micro")}
                <div className="flex gap-2">{CompagniaBtn("micro")}{PrintBtn}{SaveBtn("micro")}</div>
              </div>
              {m.compagnia_open && (
                <Section title="Quantificazione Compagnia (controparte)">
                  <Textarea value={m.compagnia_note || ""} onChange={(e) => setT("micro", { compagnia_note: e.target.value })} data-testid="quant-micro-compagnia-note" className="bg-slate-900/70 border-slate-700 text-slate-100" rows={3} placeholder="Inserisci la quantificazione proposta dalla compagnia a confronto…" />
                </Section>
              )}
            </TabsContent>

            {/* ---------- TAB 3 MACRO ---------- */}
            <TabsContent value="macro" className="mt-0">
              {TabelleInfo({ agg: yr.aggiornamento?.macro, fonte: tab.fonti?.macro, testKey: "macro", anno, anni: tab.anni, onAnno: changeAnno })}
              <div className="grid lg:grid-cols-2 gap-4">
                <div className="space-y-4">
                  <Section title="Dati">
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Età" value={M.eta} onChange={(v) => setT("macro", { eta: v })} title={(!prefill.eta && !M.eta) ? "Inserire data di nascita cliente e data sinistro nella pratica" : undefined} hint="auto da data nascita e data sinistro" testId="quant-macro-eta" />
                      <Field label="Reddito" value={M.reddito} onChange={(v) => setT("macro", { reddito: v })} suffix="€" testId="quant-macro-reddito" />
                    </div>
                  </Section>
                  <Section title="Danno non patrimoniale (ex biologico)">
                    <div className="grid grid-cols-3 gap-3">
                      <label>
                        <span className="block text-[11px] uppercase tracking-wide text-slate-400 mb-1">Tabella</span>
                        <select value={M.tabella || "TUN"} onChange={(e) => setT("macro", { tabella: e.target.value })} data-testid="quant-macro-tabella" className="w-full h-9 rounded-md bg-slate-900/70 border border-slate-700 text-slate-100 px-2 text-sm">
                          <option value="TUN">T.U.N. (DPR 12/2025)</option>
                          <option value="MILANO">Tribunale di Milano</option>
                        </select>
                      </label>
                      <label className="block">
                        <span className="block text-[11px] uppercase tracking-wide text-slate-400 mb-1">Punti</span>
                        <select value={M.punti ?? ""} onChange={(e) => setT("macro", { punti: e.target.value })} data-testid="quant-macro-punti" className="h-9 w-full bg-slate-900/70 border border-slate-700 rounded-md text-slate-100 px-3 text-sm">
                          {Array.from({ length: 91 }, (_, i) => i + 10).map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                      </label>
                      <Calc label="Totale" value={MPermBio} testId="quant-macro-perm-tot" />
                      {MPermTab != null && num(M.punti) > 0 && <div className="col-span-2 -mt-2 text-[11px] text-emerald-400" data-testid="quant-macro-da-tabella">Valore da Tabella MACRO {anno} (età {Math.round(num(M.eta))}, {num(M.punti)} punti)</div>}
                      <Field label="Punto base (mod.)" value={M.punto_base_override ?? ma.punto_base_bio} onChange={(v) => setT("macro", { punto_base_override: v })} suffix="€" hint={`coeff. età ${MCoeffEta.toFixed(3)}`} testId="quant-macro-puntobase" />
                    </div>
                  </Section>
                  <Section title="Danno non patrimoniale temporaneo">
                    <Field label="Punto base I.T.T." value={M.itt_override ?? ma.itt_giornaliero} onChange={(v) => setT("macro", { itt_override: v })} suffix="€" testId="quant-macro-itt" className="mb-2 max-w-[200px]" />
                    {DAYS.map(([k, lbl], i) => (
                      <div key={k} className="grid grid-cols-2 gap-3 mb-2">
                        <Field label={lbl} value={M[`temp_${k}`]} onChange={(v) => setT("macro", { [`temp_${k}`]: v })} testId={`quant-macro-temp-${k}`} />
                        <Calc label="Totale" value={MTemp[i].tot} testId={`quant-macro-temp-tot-${k}`} />
                      </div>
                    ))}
                  </Section>
                  <Section title="Danno morale">
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Applica a permanente %" value={M.morale_perm_pct} onChange={(v) => setT("macro", { morale_perm_pct: v })} testId="quant-macro-morale-perm" />
                      <Calc label="Totale" value={MMoralePerm} testId="quant-macro-morale-perm-tot" />
                      <Field label="Applica a temporaneo (ITT) %" value={M.morale_temp_pct} onChange={(v) => setT("macro", { morale_temp_pct: v })} testId="quant-macro-morale-temp" />
                      <Calc label="Totale" value={MMoraleTemp} testId="quant-macro-morale-temp-tot" />
                    </div>
                  </Section>
                </div>
                <div className="space-y-4">
                  <Section title="Lucro cessante">
                    <div className="grid grid-cols-3 gap-3">
                      <Field label="Giorni" value={M.lucro_giorni} onChange={(v) => setT("macro", { lucro_giorni: v })} testId="quant-macro-lucro-gg" />
                      <Field label="Redd/Gg" value={M.lucro_redd_gg} onChange={(v) => setT("macro", { lucro_redd_gg: v })} suffix="€" testId="quant-macro-lucro-redd" />
                      <Calc label="Totale" value={MLucroTot} testId="quant-macro-lucro-tot" />
                    </div>
                  </Section>
                  <Section title="Invalidità perm. specifica temporanea">
                    <div className="flex gap-4 mb-2 text-sm text-slate-300">
                      <label className="flex items-center gap-2"><input type="radio" checked={M.ipst_mode !== "fraz"} onChange={() => setT("macro", { ipst_mode: "perc" })} data-testid="quant-macro-ipst-perc" /> Percentuale</label>
                      <label className="flex items-center gap-2"><input type="radio" checked={M.ipst_mode === "fraz"} onChange={() => setT("macro", { ipst_mode: "fraz" })} data-testid="quant-macro-ipst-fraz" /> Frazionato</label>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <Field label="Valore" value={M.ipst_valore} onChange={(v) => setT("macro", { ipst_valore: v })} suffix="€" testId="quant-macro-ipst-val" />
                      <Field label="%" value={M.ipst_pct} onChange={(v) => setT("macro", { ipst_pct: v })} testId="quant-macro-ipst-pct" />
                      <Calc label="Totale" value={MIpsTempTot} testId="quant-macro-ipst-tot" />
                    </div>
                  </Section>
                  <Section title="Invalidità permanente specifica">
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Coefficiente età" value={M.ips_coeff_eta} onChange={(v) => setT("macro", { ips_coeff_eta: v })} hint="auto da tab. capitalizzazione (R.D. 1403/1922) — modificabile" testId="quant-macro-ips-coeff" />
                      <Field label="Postumi %" value={M.ips_postumi} onChange={(v) => setT("macro", { ips_postumi: v })} testId="quant-macro-ips-postumi" />
                      <Field label="Vita/lavoro %" value={M.ips_vita_lavoro} onChange={(v) => setT("macro", { ips_vita_lavoro: v })} testId="quant-macro-ips-vita" />
                      <Calc label="Totale" value={MIpsTot} testId="quant-macro-ips-tot" />
                    </div>
                  </Section>
                  <Section title="Personalizzazione danno">
                    <div className="flex gap-4 mb-3 text-sm text-slate-300">
                      <label className="flex items-center gap-2"><input type="radio" checked={!M.pers_solo_perm} onChange={() => setT("macro", { pers_solo_perm: false })} data-testid="quant-macro-pers-both" /> Danno permanente + I.T.T.</label>
                      <label className="flex items-center gap-2"><input type="radio" checked={!!M.pers_solo_perm} onChange={() => setT("macro", { pers_solo_perm: true })} data-testid="quant-macro-pers-solo" /> Solo danno permanente</label>
                    </div>
                    <div className="flex items-center gap-3">
                      <Slider value={[num(M.pers_pct)]} max={100} step={1} onValueChange={(v) => setT("macro", { pers_pct: v[0] })} data-testid="quant-macro-pers-slider" className="flex-1" />
                      <Field label="%" value={M.pers_pct} onChange={(v) => setT("macro", { pers_pct: v })} testId="quant-macro-pers-pct" className="w-24" />
                      <Calc label="Totale personalizz." value={MPersTot} testId="quant-macro-pers-tot" />
                    </div>
                  </Section>
                  <Section title="Concorso di colpa">
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="%" value={M.concorso_pct} onChange={(v) => setT("macro", { concorso_pct: v })} testId="quant-macro-concorso" />
                      <Calc label="Totale (netto concorso)" value={MConcorso} testId="quant-macro-concorso-tot" />
                    </div>
                  </Section>
                </div>
              </div>
              <Section title="Totali">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  <Calc label="Totale spese (da Spese Mediche)" value={speseTot} testId="quant-macro-rsp" />
                  <Field label="Acconto su danno ricevuto" value={M.acconto} onChange={(v) => setT("macro", { acconto: v })} suffix="€" testId="quant-macro-acconto" />
                  <Calc label="Tot. danno non patr. (ex biologico)" value={MNonPatr} testId="quant-macro-rnonpatr" />
                  <Calc label="Totale risarcimento" value={MRis} testId="quant-macro-rtot" />
                  <Field label="Arrotondamento" value={M.arrotondamento} onChange={(v) => setT("macro", { arrotondamento: v })} suffix="€" testId="quant-macro-arrot" />
                </div>
              </Section>
              <Section title="Note">
                <Textarea value={M.note || ""} onChange={(e) => setT("macro", { note: e.target.value })} data-testid="quant-macro-note" className="bg-slate-900/70 border-slate-700 text-slate-100" rows={3} />
              </Section>
              <div className="flex flex-wrap items-end gap-3 justify-between">
                {MedicoSelect("macro")}
                <div className="flex gap-2">{CompagniaBtn("macro")}{PrintBtn}{SaveBtn("macro")}</div>
              </div>
              {M.compagnia_open && (
                <Section title="Quantificazione Compagnia (controparte)">
                  <Textarea value={M.compagnia_note || ""} onChange={(e) => setT("macro", { compagnia_note: e.target.value })} data-testid="quant-macro-compagnia-note" className="bg-slate-900/70 border-slate-700 text-slate-100" rows={3} />
                </Section>
              )}
            </TabsContent>

            {/* ---------- TAB 4 MATERIALE ---------- */}
            <TabsContent value="materiale" className="mt-0 space-y-4">
              <Section title="Dati veicolo (da pratica)">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  <Field label="Veicolo danneggiato" type="text" value={mt.veicolo ?? prefill.veicolo} onChange={(v) => setT("materiale", { veicolo: v })} testId="quant-mat-veicolo" />
                  <Field label="Targa" type="text" value={mt.targa ?? prefill.targa} onChange={(v) => setT("materiale", { targa: v })} testId="quant-mat-targa" />
                  <Field label="Data immatricolazione" type="text" value={mt.data_immat ?? prefill.data_immatricolazione} onChange={(v) => setT("materiale", { data_immat: v })} testId="quant-mat-immat" />
                  <Field label="Valore antesinistro" value={mt.valore_ante} onChange={(v) => setT("materiale", { valore_ante: v })} suffix="€" testId="quant-mat-valore-ante" />
                  <label className="flex items-center gap-2 mt-6 text-sm text-slate-300">
                    <Checkbox checked={!!mt.perizia_relitto} onCheckedChange={(v) => setT("materiale", { perizia_relitto: !!v })} data-testid="quant-mat-perizia-relitto" /> Perizia a relitto
                  </label>
                </div>
              </Section>
              <Section title="Quantificazione del danno materiale">
                <div className="grid sm:grid-cols-3 gap-3">
                  <Field label="Fattura/preventivo riparatore" value={mt.fattura} onChange={(v) => setT("materiale", { fattura: v })} suffix="€" testId="quant-mat-fattura" />
                  <label>
                    <span className="block text-[11px] uppercase tracking-wide text-slate-400 mb-1">Emessa da</span>
                    <select value={mt.emessa_da || ""} onChange={(e) => setT("materiale", { emessa_da: e.target.value })} data-testid="quant-mat-emessa" className="w-full h-9 rounded-md bg-slate-900/70 border border-slate-700 text-slate-100 px-2 text-sm">
                      <option value="">—</option>
                      {carrozzieri.map((x) => <option key={x} value={x}>{x}</option>)}
                    </select>
                  </label>
                  <Field label="Quantificazione di parte" value={mt.quant_parte} onChange={(v) => setT("materiale", { quant_parte: v })} suffix="€" testId="quant-mat-quantparte" />
                </div>
              </Section>
              <Section title="Fermo tecnico">
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Giorni" value={mt.fermo_giorni} onChange={(v) => setT("materiale", { fermo_giorni: v })} testId="quant-mat-fermo-gg" />
                  <Field label="Costo giornaliero" value={mt.fermo_costo} onChange={(v) => setT("materiale", { fermo_costo: v })} suffix="€" testId="quant-mat-fermo-costo" />
                  <Calc label="Totale fermo tecnico" value={fermoTot} testId="quant-mat-fermo-tot" />
                </div>
              </Section>
              <Section title="Deprezzamento commerciale">
                <label className="flex items-center gap-2 mb-3 text-sm text-slate-300">
                  <Checkbox checked={!!mt.deprezzamento} onCheckedChange={(v) => setT("materiale", { deprezzamento: !!v })} data-testid="quant-mat-depr-toggle" /> Abilita deprezzamento
                </label>
                {mt.deprezzamento && (
                  <div className="grid sm:grid-cols-3 gap-3">
                    {[["valore_relitto", "Valore a relitto"], ["bollo", "Bollo auto non goduto"], ["recupero", "Spese recupero veicolo"], ["rottamazione", "Spese rottamazione"], ["immatricolazione", "Nuova immatricolazione"], ["passaggio", "Passaggio di proprietà"], ["spesa", "Spesa"], ["rientro", "Spese rientro al domicilio"], ["deposito", "Spese deposito auto"]].map(([k, lbl]) => (
                      <Field key={k} label={lbl} value={mt[k]} onChange={(v) => setT("materiale", { [k]: v })} suffix="€" testId={`quant-mat-${k}`} />
                    ))}
                  </div>
                )}
              </Section>
              <Section title="Totali">
                <div className="grid sm:grid-cols-2 gap-3">
                  <Calc label="Totale quantificazione (IVA compresa — Cass. III Sez. 1688/2010)" value={matBase} testId="quant-mat-tot" />
                  <div>
                    <label className="flex items-center gap-2 mb-2 text-sm text-slate-300">
                      <Checkbox checked={!!mt.corresponsabilita} onCheckedChange={(v) => setT("materiale", { corresponsabilita: !!v })} data-testid="quant-mat-corresp-toggle" /> Eventuale corresponsabilità
                    </label>
                    {mt.corresponsabilita && <Field label="Corresponsabilità %" value={mt.corresp_pct} onChange={(v) => setT("materiale", { corresp_pct: v })} testId="quant-mat-corresp-pct" className="max-w-[140px]" />}
                    <div className="mt-2"><Calc label="Totale al netto della corresponsabilità" value={matNetto} testId="quant-mat-netto" /></div>
                  </div>
                </div>
              </Section>
              <div className="flex justify-end">{SaveBtn("materiale")}</div>
            </TabsContent>

            {/* ---------- TAB 5 DATORE ---------- */}
            <TabsContent value="datore" className="mt-0 space-y-4">
              <Section title="Datore di lavoro">
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Cognome dipendente" type="text" value={dt.cognome} onChange={(v) => setT("datore", { cognome: v })} testId="quant-dat-cognome" />
                  <Field label="Nome dipendente" type="text" value={dt.nome} onChange={(v) => setT("datore", { nome: v })} testId="quant-dat-nome" />
                  <Field label="Qualifica dipendente" type="text" value={dt.qualifica} onChange={(v) => setT("datore", { qualifica: v })} testId="quant-dat-qualifica" />
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Assenza dal" type="date" value={dt.assenza_dal} onChange={(v) => setT("datore", { assenza_dal: v })} testId="quant-dat-dal" />
                    <Field label="Assenza al" type="date" value={dt.assenza_al} onChange={(v) => setT("datore", { assenza_al: v })} testId="quant-dat-al" />
                  </div>
                </div>
              </Section>
              <Section title="Voci retributive a carico ditta">
                <div className="grid sm:grid-cols-3 gap-3">
                  {[["integrazione", "Integrazione retribuzione (busta paga)"], ["tredicesima", "Ratei tredicesima"], ["quattordicesima", "Ratei quattordicesima"], ["ferie", "Ratei di ferie"], ["exfestivita", "Ratei ex festività abolite"], ["permessi", "Ratei permessi/riduzione orario"], ["festivita", "Festività"], ["tfr", "Ratei TFR"], ["inail", "Contributi INAIL carico ditta"], ["previdenziali", "Contributi previdenziali"]].map(([k, lbl]) => (
                    <Field key={k} label={lbl} value={dt[k]} onChange={(v) => setT("datore", { [k]: v })} suffix="€" testId={`quant-dat-${k}`} />
                  ))}
                </div>
              </Section>
              <Section title="Altre voci">
                {(dt.altre || []).map((r, i) => (
                  <div key={i} className="grid grid-cols-[1fr_160px] gap-3 mb-2">
                    <Field label={`Descrizione ${i + 1}`} type="text" value={r.d} onChange={(v) => { const a = [...dt.altre]; a[i] = { ...a[i], d: v }; setT("datore", { altre: a }); }} testId={`quant-dat-altre-d-${i}`} />
                    <Field label="Importo" value={r.v} onChange={(v) => { const a = [...dt.altre]; a[i] = { ...a[i], v }; setT("datore", { altre: a }); }} suffix="€" testId={`quant-dat-altre-v-${i}`} />
                  </div>
                ))}
              </Section>
              <div className="flex items-center justify-between">
                <Calc label="Totale" value={datoreTot} testId="quant-dat-tot" />
                {SaveBtn("datore")}
              </div>
            </TabsContent>

            {/* ---------- TAB 6 INAIL ---------- */}
            <TabsContent value="inail" className="mt-0 space-y-4">
              {TabelleInfo({ agg: yr.aggiornamento?.inail, fonte: tab.fonti?.inail, testKey: "inail", anno, anni: tab.anni, onAnno: changeAnno })}
              <Section title="Calcolo risarcimento INAIL">
                <div className="grid sm:grid-cols-3 gap-3">
                  <Field label="Invalidità %" value={inl.invalidita} onChange={(v) => setT("inail", { invalidita: v })} testId="quant-inail-inv" />
                  <Field label="Età" value={inl.eta} onChange={(v) => setT("inail", { eta: v })} testId="quant-inail-eta" />
                  <label>
                    <span className="block text-[11px] uppercase tracking-wide text-slate-400 mb-1">Sesso</span>
                    <select value={inl.sesso || "M"} onChange={(e) => setT("inail", { sesso: e.target.value })} data-testid="quant-inail-sesso" className="w-full h-9 rounded-md bg-slate-900/70 border border-slate-700 text-slate-100 px-2 text-sm">
                      <option value="M">Maschio</option><option value="F">Femmina</option>
                    </select>
                  </label>
                  <label>
                    <span className="block text-[11px] uppercase tracking-wide text-slate-400 mb-1">Tipo contratto</span>
                    <select value={inl.contratto || "industria"} onChange={(e) => setT("inail", { contratto: e.target.value })} data-testid="quant-inail-contratto" className="w-full h-9 rounded-md bg-slate-900/70 border border-slate-700 text-slate-100 px-2 text-sm">
                      <option value="industria">Industria: tutti i lavoratori</option>
                      <option value="agri_ind">Agricoltura: tempo indeterminato</option>
                      <option value="agri_det">Agricoltura: tempo determinato</option>
                      <option value="agri_auto">Agricoltura: autonomo</option>
                    </select>
                  </label>
                  <Field label="Retribuzione annua" value={inl.retribuzione} onChange={(v) => setT("inail", { retribuzione: v })} suffix="€" testId="quant-inail-retrib" />
                  <Field label="N° familiari" value={inl.familiari} onChange={(v) => setT("inail", { familiari: v })} testId="quant-inail-familiari" />
                  <Field label="Importo biologico da tabella INAIL" value={inl.importo_biologico} onChange={(v) => setT("inail", { importo_biologico: v })} suffix="€" hint="capitale (6-15%) o rendita (≥16%) da circolare INAIL" testId="quant-inail-bio" />
                </div>
                <div className="flex flex-wrap gap-3 mt-4">
                  <Button onClick={() => setT("inail", { calcolato: true })} data-testid="quant-inail-calcola" className="bg-blue-600 hover:bg-blue-700 text-white"><Calculator className="h-4 w-4 mr-1" /> Calcola</Button>
                  {SaveBtn("inail")}
                </div>
                {inl.calcolato && (
                  <div className="grid sm:grid-cols-3 gap-3 mt-4">
                    <Calc label={`Danno patrimoniale${inCoeff ? ` (grado ${inCoeff.grado}, coeff. ${inCoeff.coeff})` : ""}`} value={inPatr} testId="quant-inail-patr" />
                    <Calc label="Danno biologico" value={inBio} testId="quant-inail-biotot" />
                    <Calc label="Totale risarcimento INAIL" value={inTot} testId="quant-inail-tot" />
                  </div>
                )}
              </Section>
            </TabsContent>

            {/* ---------- TAB 7 CARROZZERIA ---------- */}
            <TabsContent value="carrozzeria" className="mt-0 space-y-4">
              <Section title="Pagamento carrozzeria">
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Perizia" type="text" value={cz.perizia ?? prefill.perito} onChange={(v) => setT("carrozzeria", { perizia: v })} testId="quant-cz-perizia" />
                  <Field label="Importo pattuito" value={cz.pattuito} onChange={(v) => setT("carrozzeria", { pattuito: v })} suffix="€" testId="quant-cz-pattuito" />
                  <Field label="Percentuale scorporo %" value={cz.scorporo_pct} onChange={(v) => setT("carrozzeria", { scorporo_pct: v })} testId="quant-cz-scorporo" />
                  <Calc label="Totale" value={czTot} testId="quant-cz-tot" />
                </div>
              </Section>
              <div className="flex justify-end">{SaveBtn("carrozzeria")}</div>
            </TabsContent>
          </div>
        </Tabs>
        <ListinoSpeseDialog open={listinoOpen} onOpenChange={setListinoOpen} onChanged={loadListino} />
      </div>
    </div>,
    document.body
  );
}

const TABS = [
  { k: "spese", label: "Spese Mediche" },
  { k: "micro", label: "Micro-permanenti" },
  { k: "macro", label: "Macro-permanenti" },
  { k: "materiale", label: "Materiale" },
  { k: "datore", label: "Datore" },
  { k: "inail", label: "INAIL" },
  { k: "carrozzeria", label: "Pagamento Carrozzeria" },
];

function TabelleInfo({ agg, fonte, testKey, anno, anni, onAnno }) {
  if (!agg && !(anni && anni.length)) return null;
  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-600/30 bg-amber-500/5 px-3 py-2">
      <div className="flex flex-wrap items-center gap-3">
        {anni && anni.length > 0 && (
          <label className="flex items-center gap-1.5 text-[11px] text-amber-200/90">
            Anno tabelle:
            <select value={anno} onChange={(e) => onAnno(e.target.value)} data-testid={`quant-anno-${testKey}`}
              className="h-6 rounded bg-slate-900/70 border border-amber-600/40 text-amber-200 text-[11px] px-1.5">
              {anni.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </label>
        )}
        {agg && <span className="text-[11px] text-amber-200/90" data-testid={`quant-tabelle-info-${testKey}`}>Aggiornate al: {agg}</span>}
      </div>
      {fonte && (
        <a href={fonte} target="_blank" rel="noreferrer" data-testid={`quant-tabelle-cerca-${testKey}`}
          className="inline-flex items-center gap-1 text-[11px] text-amber-300 hover:text-amber-200 underline">
          <RefreshCw className="h-3 w-3" /> Cerca aggiornamento tabelle <ExternalLink className="h-3 w-3" />
        </a>
      )}
    </div>
  );
}

const escape2 = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

function buildPrintRows(k, ctx) {
  const e = (n) => eur(n);
  if (k === "spese") {
    const rows = (ctx.q.spese.righe || []).map((r) => [r.descrizione || "—", e(num(r.spese))]);
    rows.push(["TOTALE SPESE", e(ctx.speseTot)]);
    return rows;
  }
  if (k === "micro") return [
    ["Danno permanente", e(ctx.mPermTot)], ["Aggravamento", e(ctx.mAggTot)], ["Danno temporaneo", e(ctx.mTempTot)],
    ["Danno morale", e(ctx.mMoraleTot)], ["Lucro cessante", e(ctx.mLucroTot)], ["Invalidità perm. specifica", e(ctx.mIpsTot)],
    ["Totale spese mediche", e(ctx.speseTot)], ["Tot. danno non patrimoniale", e(ctx.mNonPatr)], ["Totale quantificazione", e(ctx.mConcorso)],
    ["Totale finale", e(ctx.mFinale)],
  ];
  if (k === "macro") return [
    ["Danno permanente (biologico)", e(ctx.MPermBio)], ["Danno temporaneo", e(ctx.MTempTot)],
    ["Danno morale permanente", e(ctx.MMoralePerm)], ["Danno morale temporaneo", e(ctx.MMoraleTemp)],
    ["Personalizzazione", e(ctx.MPersTot)], ["Lucro cessante", e(ctx.MLucroTot)], ["Invalidità perm. specifica", e(ctx.MIpsTot)],
    ["Totale spese mediche", e(ctx.speseTot)], ["Tot. danno non patrimoniale", e(ctx.MNonPatr)], ["Totale risarcimento", e(ctx.MRis)],
  ];
  if (k === "materiale") return [["Fermo tecnico", e(ctx.fermoTot)], ["Deprezzamento", e(ctx.deprTot)], ["Totale al netto corresponsabilità", e(ctx.matNetto)]];
  if (k === "datore") return [["Totale", e(ctx.datoreTot)]];
  if (k === "inail") return [["Danno patrimoniale", e(ctx.inPatr)], ["Danno biologico", e(ctx.inBio)], ["Totale INAIL", e(ctx.inTot)]];
  if (k === "carrozzeria") return [["Totale", e(ctx.czTot)]];
  return [];
}
